import {
  EMPTY,
  merge,
  type ObservedValueOf,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from "rxjs"
import { HTML_PREFIX } from "../../constants"
import type { Reader } from "../../reader"
import {
  removeAttributeIfPresent,
  removeStylePropertyIfPresent,
  setAttributeIfChanged,
  setStylePropertyIfChanged,
} from "../../utils/dom"
import { ReactiveEntity } from "../../utils/ReactiveEntity"
import { constrainPositionWithinViewport } from "./constraints"
import {
  applyViewportTransformForControlledMode,
  derivePositionFromScaleForControlledMode,
} from "./controlled"
import { applyScaleToViewportForScroll } from "./scrollable"
import type { ZoomPosition } from "./types"

export type ZoomControllerState = {
  isZooming: boolean
  currentScale: number
  /**
   * Represents the origin translate position before zoom is applied
   * and is based of an origin of 0,0.
   */
  currentPosition: ZoomPosition
}

/**
 * @important
 * Animation is not possible for scrolling mode because the animation
 * is based on transform origin and its impossible to have a correct
 * centered transform origin with the viewport in a scrollable container.
 * What we do is compute the "wanted" scroll position to make it look like
 * scale down/up appears centered.
 */
const ANIMATION_DURATION = 200

export class ZoomController extends ReactiveEntity<ZoomControllerState> {
  private enterSubject = new Subject<
    | undefined
    | {
        element?: HTMLImageElement
        scale?: number
        animate?: boolean
      }
  >()
  private exitSubject = new Subject<{ animate?: boolean } | undefined>()

  constructor(protected reader: Reader) {
    super({
      isZooming: false,
      currentScale: 1,
      currentPosition: { x: 0, y: 0 },
    })

    const enter$ = this.enterSubject.pipe(
      switchMap((options) => {
        const { scale = 1, animate = false } = options ?? {}
        const viewportElement = this.reader.viewport.value.element

        setAttributeIfChanged(
          this.viewport.element,
          `data-${HTML_PREFIX}-zooming`,
          "true",
        )

        const scrollNavigationElement =
          this.scrollNavigationController.value.element
        if (scrollNavigationElement) {
          setAttributeIfChanged(
            scrollNavigationElement,
            `data-${HTML_PREFIX}-zooming`,
            this.isControlled ? "false" : "true",
          )
        }

        if (animate && this.isControlled) {
          setStylePropertyIfChanged(
            viewportElement.style,
            `transition`,
            `transform ${ANIMATION_DURATION}ms`,
          )
        }

        this.mergeCompare({
          isZooming: true,
          currentScale: 1,
          currentPosition: { x: 0, y: 0 },
        })

        this.updateZoom(scale)

        if (scale !== 1 && this.isControlled) {
          return timer(options?.animate ? ANIMATION_DURATION : 0).pipe(
            tap(() => {
              this.reader.layout()
            }),
          )
        }

        return EMPTY
      }),
    )

    const exit$ = this.exitSubject.pipe(
      switchMap((options) => {
        const viewportElement = this.reader.viewport.value.element

        setAttributeIfChanged(
          this.viewport.element,
          `data-${HTML_PREFIX}-zooming`,
          "false",
        )

        const scrollNavigationElement =
          this.scrollNavigationController.value.element
        if (scrollNavigationElement) {
          removeAttributeIfPresent(
            scrollNavigationElement,
            `data-${HTML_PREFIX}-zooming-direction`,
          )
          setAttributeIfChanged(
            scrollNavigationElement,
            `data-${HTML_PREFIX}-zooming`,
            "false",
          )
        }

        if (options?.animate && this.isControlled) {
          setStylePropertyIfChanged(
            viewportElement.style,
            `transition`,
            `transform ${ANIMATION_DURATION}ms`,
          )
        }

        this.updateZoom(1, { x: 0, y: 0 })

        removeStylePropertyIfPresent(viewportElement.style, `transform`)

        this.mergeCompare({
          isZooming: false,
        })

        return timer(options?.animate ? ANIMATION_DURATION : 0).pipe(
          tap(() => {
            const viewportElement = this.reader.viewport.value.element
            removeStylePropertyIfPresent(
              viewportElement.style,
              `transform-origin`,
            )
            removeStylePropertyIfPresent(viewportElement.style, `transition`)

            if (this.isControlled) {
              this.reader.layout()
            }
          }),
          takeUntil(this.enterSubject),
        )
      }),
    )

    merge(enter$, exit$).pipe(takeUntil(this.destroy$)).subscribe()
  }

  public enter(options?: ObservedValueOf<typeof this.enterSubject>) {
    this.enterSubject.next(options)
  }

  public exit(options?: ObservedValueOf<typeof this.exitSubject>) {
    this.exitSubject.next(options)
  }

  public move(
    position: { x: number; y: number },
    options?: { constrain?: "within-viewport" },
  ) {
    // no moving needed for scrollable mode
    if (!this.isControlled) return

    // make sure to prevent animation before applying the new position
    removeStylePropertyIfPresent(this.viewport.element.style, `transition`)

    this.updateZoom(this.value.currentScale, position, {
      constraints:
        options?.constrain === "within-viewport"
          ? (...rest) =>
              constrainPositionWithinViewport(...rest, this.reader.viewport)
          : undefined,
    })
  }

  public scaleAt(
    userScale: number,
    options?: { constrain?: "within-viewport" },
  ): void {
    if (!this.value.isZooming) return

    // make sure to prevent animation before applying the new position
    removeStylePropertyIfPresent(this.viewport.element.style, `transition`)

    const roundedScale = Math.ceil(userScale * 100) / 100
    const newScale = roundedScale

    this.updateZoom(newScale, undefined, {
      constraints:
        options?.constrain === "within-viewport"
          ? (...rest) =>
              constrainPositionWithinViewport(...rest, this.reader.viewport)
          : undefined,
    })
  }

  protected updateZoom(
    scale: number,
    position?: ZoomPosition,
    options?: {
      constraints?: (position: ZoomPosition, scale: number) => ZoomPosition
    },
  ) {
    if (this.isControlled) {
      const newPosition = position
        ? position
        : derivePositionFromScaleForControlledMode(
            this.value.currentScale,
            scale,
            this.viewport.element,
            this.value.currentPosition,
          )

      const constrainedPosition =
        options?.constraints?.(newPosition, scale) ?? newPosition

      this.applyZoom(scale, constrainedPosition)

      return this.mergeCompare({
        currentScale: scale,
        currentPosition: constrainedPosition,
      })
    }

    this.applyZoom(scale, position ?? this.value.currentPosition)

    this.mergeCompare({
      currentScale: scale,
    })
  }

  protected applyZoom(scale: number, position: { x: number; y: number }) {
    if (!this.isControlled) {
      /**
       * @important
       * This does trigger a navigation to always re-center the scroll position with the new scale
       */
      applyScaleToViewportForScroll(scale, this.reader)
    } else {
      applyViewportTransformForControlledMode(
        scale,
        position,
        this.viewport.element,
      )
    }

    this.reader.viewport.layout()
  }

  protected get isControlled() {
    return this.reader.settings.values.computedPageTurnMode === "controlled"
  }

  protected get scrollNavigationController() {
    return this.reader.navigation.scrollNavigationController
  }

  protected get viewport() {
    return this.reader.viewport.value
  }
}
